import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import toast from "react-hot-toast";

import Axios from "../../services/axios";
import { setCounterStatus } from "./CounterSlice";
import { CurrentVersion, REACT_APP_BACKEND_URL_TEST } from "../../config";

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ payload, navigate }, { rejectWithValue, dispatch }) => {
    try {
      const response = await Axios.post(`/v3/login`, payload);
      const isNurseRole = response?.data?.user?.role?.id == 4;
      navigate(isNurseRole ? "/visits" : "/dashboard");
      // if (response?.data?.permissions_by_module?.RCM) {
      //   if (response?.data?.subscription?.rcm_check) {
      //     Axios.post(
      //       `${REACT_APP_BACKEND_URL_TEST}/v3/RCM/generateApi`,
      //       {},
      //       {
      //         headers: {
      //           Authorization: `Bearer ${response?.data?.token}`,
      //           "current-version": CurrentVersion,
      //         },
      //       }
      //     );
      //   }
      // }
      if (response?.data?.permissions_by_module?.counter) {
        Axios.post(
          `${REACT_APP_BACKEND_URL_TEST}/v3/counter/getReportByAuthUser`,
          {},
          {
            headers: {
              Authorization: `Bearer ${response?.data?.token}`,
              "current-version": CurrentVersion,
            },
          }
        )
          .then((counterResponse) => {
            if (counterResponse?.data) {
              dispatch(setCounterStatus(counterResponse.data?.counter?.status));
            }
          })
          .catch(() => {
            dispatch(setCounterStatus("closed"));
          });
      }
      return response.data;
    } catch (error) {
      toast.error(
        error?.response?.error ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Login failed"
      );
      return rejectWithValue(error.response?.data || "Login failed");
    }
  }
);

const initialState = {
  isLoading: false,
  user: null,
  token: null,
  permissions: [],
  roleData: null,
  subscription: null,
  buisnessInfo: null,
  zatcaSettings: {
    is_zatca: false,
    enable_send_cash_invoices: false,
    enable_send_insurance_invoices: false,
    enable_send_corporate_invoices: false,
    enable_send_government_invoices: false,
    enable_send_deposit_invoices: false,
    enable_send_withdraw_invoices: false,
    enable_edit_zatca_posted_invoice: false,
  },
  invoiceSettings: {
    allow_update_after_erp_posting: false,
    paybill_settings: { display_mode: "category_tabs" },
  },
  insuranceApprovalSettings: {
    auto_send_institutional: true,
    auto_send_pharmacy: true,
    auto_send_oral: true,
    auto_send_vision: true,
    auto_send_professional: true,
  },
  visitSettings: {
    separate_walkin_and_slots: false,
  },
  claimsSettings: {
    include_zero_price_items: false,
  },
  medicalReportSettings: {
    template: "standard_medical_report",
    show_business_logo: true,
    show_chai_logo: false,
    show_vision_2030_logo: false,
    show_custom_logo: false,
    chai_logo_url: "",
    vision_2030_logo_url: "",
    custom_logo_url: "",
  },
  prescriptionSettings: {
    items: true,
    diagnosis: true,
    vital_signs: true,
  },
  discountSettings: { default_limit: 100, department_limits: [] },
  glPostingSettings: { revenue_grouping_mode: "department" },
};

const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.isLoading = false;
      state.user = null;
      state.token = null;
      localStorage.clear()
      sessionStorage.clear()
    },
    updateModulePermissions: (state, action) => {
      state.permissions = action?.payload;
    },
    updateZatcaSettings: (state, action) => {
      state.zatcaSettings = {
        ...state.zatcaSettings,
        ...action.payload,
      };
    },
    updateInvoiceSettings: (state, action) => {
      state.invoiceSettings = {
        ...state.invoiceSettings,
        ...action.payload,
      };
    },
    updateInsuranceApprovalSettings: (state, action) => {
      state.insuranceApprovalSettings = {
        ...state.insuranceApprovalSettings,
        ...action.payload,
      };
    },
    updatePayBillSettings: (state, action) => {
      state.invoiceSettings.paybill_settings = {
        ...state.invoiceSettings.paybill_settings,
        ...action.payload,
      };
    },
    updateVisitSettings: (state, action) => {
      state.visitSettings = {
        ...state.visitSettings,
        ...action.payload,
      };
      if (state.user) {
        state.user.settings = {
          ...(state.user.settings || {}),
          visit_settings: state.visitSettings,
        };
      }
    },
    updateClaimsSettings: (state, action) => {
      state.claimsSettings = { ...state.claimsSettings, ...action.payload };
    },
    updateMedicalReportSettings: (state, action) => {
      state.medicalReportSettings = {
        ...state.medicalReportSettings,
        ...action.payload,
      };
    },
    updatePrescriptionSettings: (state, action) => {
      state.prescriptionSettings = {
        ...state.prescriptionSettings,
        ...action.payload,
      };
      if (state.user) {
        state.user.settings = {
          ...(state.user.settings || {}),
          prescription_settings: state.prescriptionSettings,
        };
      }
    },
    updateDiscountSettings: (state, action) => {
      state.discountSettings = { ...state.discountSettings, ...action.payload };
    },
    updateGlPostingSettings: (state, action) => {
      state.glPostingSettings = {
        ...state.glPostingSettings,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload?.user;
        state.token = action.payload?.token;
        state.permissions = action.payload?.permissions_by_module;
        state.roleData = action.payload?.role_data;
        state.subscription = action.payload?.subscription;
        state.buisnessInfo = action.payload?.business;
        if (action.payload?.settings) {
          const {
            insurance_approval_settings,
            visit_settings,
            claims_settings,
            medical_report_settings,
            prescription_settings,
            discount_settings,
            paybill_settings,
            gl_posting_settings,
            ...zatcaSettings
          } = action.payload.settings;
          state.zatcaSettings = {
            ...initialState.zatcaSettings,
            ...zatcaSettings,
          };
          state.insuranceApprovalSettings = {
            ...initialState.insuranceApprovalSettings,
            ...(insurance_approval_settings || {}),
          };
          state.visitSettings = {
            ...initialState.visitSettings,
            ...(visit_settings || {}),
          };
          state.claimsSettings = {
            ...initialState.claimsSettings,
            ...(claims_settings || {}),
          };
          state.medicalReportSettings = {
            ...initialState.medicalReportSettings,
            ...(medical_report_settings || {}),
          };
          state.prescriptionSettings = {
            ...initialState.prescriptionSettings,
            ...(prescription_settings || {}),
          };
          state.discountSettings = {
            ...initialState.discountSettings,
            ...(discount_settings || {}),
          };
          state.invoiceSettings = {
            ...initialState.invoiceSettings,
            paybill_settings: {
              ...initialState.invoiceSettings.paybill_settings,
              ...(paybill_settings || {}),
            },
          };
          state.glPostingSettings = {
            ...initialState.glPostingSettings,
            ...(gl_posting_settings || {}),
          };
          state.user = {
            ...(state.user || {}),
            settings: {
              ...(state.user?.settings || {}),
              visit_settings: state.visitSettings,
            },
          };
        }
      })
      .addCase(loginUser.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const {
  logout,
  updateModulePermissions,
  updateZatcaSettings,
  updateInvoiceSettings,
  updatePayBillSettings,
  updateInsuranceApprovalSettings,
  updateVisitSettings,
  updateClaimsSettings,
  updateMedicalReportSettings,
  updatePrescriptionSettings,
  updateDiscountSettings,
  updateGlPostingSettings,
} = AuthSlice.actions;
export default AuthSlice.reducer;
